import React, { useCallback, useState } from 'react';
import 'react-day-picker/lib/style.css';
import { Button, Flexer } from '@ontruck/orc';
import ReactTooltip from 'react-tooltip';
import copy from 'copy-to-clipboard';
import { shipperContactsStyles, StyledTooltip } from './styled';
import { CopyableLink } from 'modules/Common/Copy';
import { injectIntl, FormattedMessage } from 'react-intl';

const ShipperContacts = ({ className, shipperId, owner = {}, contacts = [], intl }) => {
  const t = intl.messages.modules.shipper_order.shipper_contacts;
  const [otherContactsShown, setOtherContactsShown] = useState(false);
  const contactsInAlwaysCC = contacts.filter(({ id, always_cc }) => id !== owner?.id && always_cc);
  const countContactsInAlwaysCC = contactsInAlwaysCC.length;
  const otherContacts = contacts.filter(({ id, always_cc }) => id !== owner?.id && !always_cc);
  const countOtherContacts = otherContacts?.length;
  const countAllContacts = contacts?.length;

  const onShowContactsDetails = () => {
    ReactTooltip.show();
  };

  const onCopyEmailsToClipboard = (contactsList) => {
    const toCopy = contactsList.map(({ email }) => email).join(', ');
    copy(toCopy);
  };

  const onShowOtherContactsClick = useCallback(() => {
    setOtherContactsShown(!otherContactsShown);
  }, [setOtherContactsShown, otherContactsShown]);

  const contactsRenderer = (contactsList) =>
    contactsList.map((contact, i) => (
      <Flexer.Parent mTop="XS" key={i}>
        <div className="shipper-contacts-details__name">{contact.name}</div>
        <div className="shipper-contacts-details__contact-instructions">
          {contact.contact_instructions || '-'}
        </div>
        <div className="shipper-contacts-details__phone">
          {contact.phone ? <CopyableLink text={contact.phone} /> : '-'}
        </div>
        <div className="shipper-contacts-details__email">
          {contact.email ? <CopyableLink text={contact.email} /> : '-'}
        </div>
      </Flexer.Parent>
    ));

  const tooltipId = shipperId + 'shipper-contacts-details';
  return (
    <div className={className} data-testid="shipper-contacts">
      {/* Contacts header */}
      {countContactsInAlwaysCC + countOtherContacts > 0 && (
        <Flexer.Parent justifyContent="space-between">
          <Flexer.Child className="shipper-contacts_other-contacts">
            {`${countContactsInAlwaysCC} ${t.other_contacts_in_always_cc}`}
          </Flexer.Child>
          <Flexer.Child>
            <Button
              nude
              text={t.show_contacts_details}
              onClick={onShowContactsDetails}
              data-tip
              data-event="click"
              data-for={tooltipId}
              data-testid="shipper-contacts_show-details"
            />
          </Flexer.Child>
          <Flexer.Child />
          <Flexer.Child />
          <Flexer.Child>
            <CopyableLink
              text={`${t.copy} ${countContactsInAlwaysCC + 1} ${t.email}`}
              onClick={() => onCopyEmailsToClipboard([owner, ...contactsInAlwaysCC])}
              weight="bold"
            />
          </Flexer.Child>
        </Flexer.Parent>
      )}

      {/* Contacts details tooltip */}
      <StyledTooltip
        id={tooltipId}
        type="light"
        effect="solid"
        place="bottom"
        globalEventOff="click"
        clickable
      >
        <div className="shipper-contacts-details">
          {/* Owner */}
          <div className="shipper-contacts-details__container">
            <span className="shipper-contacts-details__title">{t.contact_who_created_this_so}</span>
            {contactsRenderer([owner])}
          </div>

          {/* Always CC contacts */}
          {countContactsInAlwaysCC > 0 && (
            <div className="shipper-contacts-details__container">
              <Flexer.Parent alignItems="center">
                <Flexer.Child flexGrow={1}>
                  <span className="shipper-contacts-details__title">{`${t.contacts_always_in_cc} (${countContactsInAlwaysCC})`}</span>
                </Flexer.Child>
                <Button
                  nude
                  className="shipper-contacts__copy-btn"
                  onClick={() => onCopyEmailsToClipboard(contactsInAlwaysCC)}
                >
                  <FormattedMessage
                    id="copy_n_emails_2"
                    defaultMessage={t.copy_n_emails}
                    values={{
                      emails: countContactsInAlwaysCC,
                    }}
                  />
                </Button>
              </Flexer.Parent>
              {contactsRenderer(contactsInAlwaysCC)}
            </div>
          )}

          {/* Other contacts */}
          {countOtherContacts > 0 && (
            <div className="shipper-contacts-details__container">
              <Flexer.Parent alignItems="center">
                <Flexer.Child flexGrow={1}>
                  <span className="shipper-contacts-details__title">{`${t.other_contacts} (${countOtherContacts})`}</span>
                  <Button
                    nude
                    className="shipper-contacts-details__other-toggle"
                    text={otherContactsShown ? t.hide : t.show}
                    onClick={onShowOtherContactsClick}
                    data-testid="shipper-contacts_other-contacts-btn"
                  />
                </Flexer.Child>
                <Button
                  nude
                  className="shipper-contacts__copy-btn"
                  onClick={() => onCopyEmailsToClipboard(contacts)}
                  data-testid="shipper-contacts_copy-all"
                >
                  <FormattedMessage
                    id="copy_all_emails"
                    defaultMessage={t.copy_all_emails}
                    values={{
                      emails: countAllContacts,
                    }}
                  />
                </Button>
              </Flexer.Parent>
              {otherContactsShown && contactsRenderer(otherContacts)}
            </div>
          )}
        </div>
      </StyledTooltip>
    </div>
  );
};

export default shipperContactsStyles(injectIntl(ShipperContacts));
